export type AsyncDebounceScheduler<CallbackNode = any> = {
    schedule: (callback: () => void) => CallbackNode;
    cancel: (node: CallbackNode) => void;
};

export type AsyncDebounceOptions<CallbackNode = any> = {
    /**
     * Maximum time to wait before triggering
     * @default 0
     */
    maxWait?: number;

    /**
     * If true, the callback will be executed immediately if it's the first call.
     * @default false
     */
    immediate?: boolean;

    /**
     * Scheduler to use for the debounce.
     */
    scheduler?: AsyncDebounceScheduler<CallbackNode>;
};

export type AsyncDeferredCallback<T> = () => T;

export type AsyncDebounceFn<T> = ((fn: AsyncDeferredCallback<T>) => void) & {
    /**
     * Execute without waiting.
     */
    flush: () => T | undefined;

    /**
     * Cancel upcoming execution.
     */
    cancel: () => void;
};

const immediateScheduler: AsyncDebounceScheduler = {
    schedule: (callback) => {
        callback();
    },
    cancel: () => {
        // No cancellation needed
    },
};

/**
 * Create a smart debounced function that will execute the passed callback
 */
export function asyncDebounce<T, CallbackNode = any>(
    delay: number,
    options: AsyncDebounceOptions<CallbackNode> = {}
): AsyncDebounceFn<T> {
    const { maxWait = 0, scheduler = immediateScheduler } = options;

    let scheduledNode: CallbackNode | null = null;
    let timeout: NodeJS.Timeout | null = null;
    let pendingExecution: AsyncDeferredCallback<T> | null = null;
    let latestExecution: number | null = null;

    // @ts-ignore
    const debounced: AsyncDebounceFn<T> = (fn: AsyncDeferredCallback<T>) => {
        pendingExecution = () => {
            pendingExecution = null;

            latestExecution = Date.now();
            const result = fn();

            scheduledNode = null;
            timeout = null;

            return result;
        };

        debounced.cancel();

        if (
            (maxWait && latestExecution && latestExecution < Date.now() - maxWait) ||
            (options.immediate && !latestExecution)
        ) {
            pendingExecution();
        } else {
            timeout = setTimeout(() => {
                scheduledNode = scheduler.schedule(() => {
                    if (pendingExecution) {
                        pendingExecution();
                    }
                });
            }, delay);
        }
    };

    debounced.cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
        }

        if (scheduledNode) {
            scheduler.cancel(scheduledNode);
        }
    };

    debounced.flush = () => {
        debounced.cancel();

        if (pendingExecution) {
            return pendingExecution();
        }
    };

    return debounced;
}
